import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Layers3,
  Loader2,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ThirdWBSAPI } from "../services/api";
import { Download } from "lucide-react";
import MetricsExcelSheet from "./MetricsExcelSheet";

type Row = Record<string, any>;

export interface MetricsAccordionItem {
  accordionKey: string;
  geometry: string;
  distinctWorkMeasureCount: number;
  workMeasureSignature: string;
}

export interface MetricsColumnItem {
  accordionKey: string;
  columnName: string;
  columnCaption: string;
  columnOrder?: number;
}

export interface MetricsAccordionHandle {
  exportExcel: () => void;
}

type GridRow = {
  childName?: string;      // <-- ADD
  wgName?: string | number;
  workName?: string;
  notation?: string;
  takeOff?: string;
  quantity?: string | number;
  cells: Record<string, any>;
};

type Props = {
  activityId: number | null;
  wbsId: number | null;
};

const EXCEL_DEFAULT_COL_WIDTH = 130;
const MIN_WIDTH = 60;
//const TABLE_MAX_HEIGHT = 320;

const MetricsAccordionTable = forwardRef<MetricsAccordionHandle, Props>(({ activityId, wbsId }, ref) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [accordionList, setAccordionList] = useState<MetricsAccordionItem[]>([]);
  const [columnMeta, setColumnMeta] = useState<MetricsColumnItem[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  
  /* const [selectedCell, setSelectedCell] = useState<{
  row: number;
  column: string;
} | null>(null); 
 */
const [selectionStart, setSelectionStart] = useState<{
    accordionKey: string;
  row: number;
  column: string;
} | null>(null);

const [selectionEnd, setSelectionEnd] = useState<{
   accordionKey: string;
  row: number;
  column: string;
} | null>(null);

const [isSelecting, setIsSelecting] = useState(false);

const [selectAllTable, setSelectAllTable] = useState<string | null>(null);


useEffect(() => {
  console.log("selectionStart changed:", selectionStart);
}, [selectionStart]);

 const resizeRef = useRef<{
  key: string;
  startX: number;
  startWidth: number;
} | null>(null);

const dragStartedRef = useRef(false);
const lastClickRef = useRef(0);

const clickColumnRef = useRef("");

const autoFitRef = useRef<{
  accordionKey: string;
  columnKey: string;
  headerText: string;
  values: any[];
} | null>(null);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      if (!activityId || !wbsId) {
        setAccordionList([]);
        setColumnMeta([]);
        setRows([]);
        setError("");
        setOpenKeys(new Set());
        setColumnWidths({});
        return;
      }

      try {
        setLoading(true);
        setError("");

        const res = await ThirdWBSAPI.getTakeoffAccordionMetrix(activityId, wbsId);

        const acc = res?.accordionList ?? res?.AccordionList ?? [];
        const cols = res?.columnMeta ?? res?.ColumnMeta ?? [];
        const data = res?.data ?? res?.Data ?? [];

        setAccordionList(
          acc.map((x: any) => ({
            accordionKey: x.accordionKey ?? x.AccordionKey ?? "",
            geometry: x.geometry ?? x.Geometry ?? "",
            distinctWorkMeasureCount:
              x.distinctWorkMeasureCount ?? x.DistinctWorkMeasureCount ?? 0,
            workMeasureSignature:
              x.workMeasureSignature ?? x.WorkMeasureSignature ?? "",
          }))
        );

        setColumnMeta(
          cols
            .map((x: any) => ({
              accordionKey: x.accordionKey ?? x.AccordionKey ?? "",
              columnName: x.columnName ?? x.ColumnName ?? "",
              columnCaption: x.columnCaption ?? x.ColumnCaption ?? "",
              columnOrder: x.columnOrder ?? x.ColumnOrder,
            }))
            .sort((a: MetricsColumnItem, b: MetricsColumnItem) => {
              const ao = a.columnOrder ?? 0;
              const bo = b.columnOrder ?? 0;
              return ao - bo;
            })
        );

        /* setRows(data);
        setOpenKeys(new Set());
        setColumnWidths({});
 */
setRows(data);

setOpenKeys(
  new Set(
    acc.map(
      (x: any) =>
        x.accordionKey ?? x.AccordionKey ?? ""
    )
  )
);

setColumnWidths({});

      } catch (e) {
        console.error(e);
        setAccordionList([]);
        setColumnMeta([]);
        setRows([]);
        setError("Failed to load metrics data.");
        setColumnWidths({});
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [activityId, wbsId]);

  const groupedRows = useMemo(() => {
    const map: Record<string, Row[]> = {};
    for (const row of rows) {
      const key = String(row.AccordionKey ?? row.accordionKey ?? "");
      if (!map[key]) map[key] = [];
      map[key].push(row);
    }
    return map;
  }, [rows]);

  const groupedColumns = useMemo(() => {
    const map: Record<string, MetricsColumnItem[]> = {};
    for (const col of columnMeta) {
      const key = String(col.accordionKey ?? "");
      if (!map[key]) map[key] = [];
      map[key].push(col);
    }
    return map;
  }, [columnMeta]);


  const maxDynamicColumns = useMemo(() => {
  return Math.max(
    0,
    ...accordionList.map(
      (acc) => (groupedColumns[acc.accordionKey] ?? []).length
    )
  );
}, [accordionList, groupedColumns]);

  const gridRowsByAccordion = useMemo(() => {
    const result: Record<string, GridRow[]> = {};

    for (const acc of accordionList) {
      const itemRows = groupedRows[acc.accordionKey] ?? [];
      const rowMap = new Map<string, GridRow>();

      for (const r of itemRows) {
        const rowKey = [
          r.ChildName ?? r.childName ?? "",
          r.WGName ?? r.wgName ?? "",
          r.WorkName ?? r.workName ?? "",
          r.Notation ?? r.notation ?? "",
          r.TakeOff ?? r.takeOff ?? "",
          r.Quantity ?? r.quantity ?? "",
        ].join("|");

        if (!rowMap.has(rowKey)) {
          rowMap.set(rowKey, {
             childName: r.ChildName ?? r.childName,
            wgName: r.WGName ?? r.wgName,
            workName: r.WorkName ?? r.workName,
            notation: r.Notation ?? r.notation,
            takeOff: r.TakeOff ?? r.takeOff,
            quantity: r.Quantity ?? r.quantity,
            cells: {},
          });
        }

        const colName = r.ColumnName ?? r.columnName;
        if (colName) {
          rowMap.get(rowKey)!.cells[String(colName)] = r.Value ?? r.value;
        }
      }

      result[acc.accordionKey] = Array.from(rowMap.values());
    }

    return result;
  }, [accordionList, groupedRows]);

  const toggle = (key: string) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => 
  {
 /*  if (!selectedCell) return;

  const currentRow = selectedCell.row; */
  if (!selectionEnd) return;

const currentRow = selectionEnd.row;

  /*  const allColumns = [

     "__rowNumber__",

    "childName",
    "wgName",
    "workName",
    "notation",
    "takeOff",
    "quantity",
    ...columnMeta.map((c) => c.columnName),
  ];  */
const rows =
  gridRowsByAccordion[selectionEnd.accordionKey] ?? [];

const columns =
  groupedColumns[selectionEnd.accordionKey] ?? [];

const childNames = Array.from(
  new Set(
    rows
      .map((r) => r.childName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const wgNames = Array.from(
  new Set(
    rows
      .map((r) => r.wgName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const workNames = Array.from(
  new Set(
    rows
      .map((r) => r.workName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showChildColumn = childNames.length > 1;
const showWGColumn = wgNames.length > 1;
const showWorkColumn = workNames.length > 1;

const allColumns = [
  "__rowNumber__",

  ...(showChildColumn ? ["childName"] : []),
  ...(showWGColumn ? ["wgName"] : []),
  ...(showWorkColumn ? ["workName"] : []),

  "notation",
  "takeOff",
  "quantity",

  ...columns.map((c) => c.columnName),
];


  //const currentColIndex = allColumns.indexOf(selectedCell.column);
  const currentColIndex = allColumns.indexOf(selectionEnd.column);

  if (e.shiftKey && selectionStart && selectionEnd) {
  e.preventDefault();

  switch (e.key) {
    case "ArrowDown":
      setSelectionEnd({
        accordionKey: selectionEnd.accordionKey,
        row: selectionEnd.row + 1,
        column: selectionEnd.column,
      });
      return;

    case "ArrowUp":
      setSelectionEnd({
        accordionKey: selectionEnd.accordionKey,
        row: Math.max(0, selectionEnd.row - 1),
        column: selectionEnd.column,
      });
      return;

    case "ArrowRight":
      if (currentColIndex < allColumns.length - 1) {
        setSelectionEnd({
          accordionKey: selectionEnd.accordionKey,
          row: selectionEnd.row,
          column: allColumns[currentColIndex + 1],
        });
      }
      return;

    case "ArrowLeft":
      if (currentColIndex > 0) {
        setSelectionEnd({
          accordionKey: selectionEnd.accordionKey,
          row: selectionEnd.row,
          column: allColumns[currentColIndex - 1],
        });
      }
      return;
  }
}

  switch (e.key) {

    case "Enter":
  e.preventDefault();

  setSelectionStart({
    accordionKey: selectionEnd.accordionKey,
    row: currentRow + 1,
    column: selectionEnd.column,
  });

  setSelectionEnd({
    accordionKey: selectionEnd.accordionKey,
    row: currentRow + 1,
    column: selectionEnd.column,
  });

  break;

case "Tab":
  e.preventDefault();

  if (e.shiftKey) {
    if (currentColIndex > 0) {
      setSelectionStart({
        accordionKey: selectionEnd.accordionKey,
        row: currentRow,
        column: allColumns[currentColIndex - 1],
      });

      setSelectionEnd({
        accordionKey: selectionEnd.accordionKey,
        row: currentRow,
        column: allColumns[currentColIndex - 1],
      });
    }
  } else {
    if (currentColIndex < allColumns.length - 1) {
      setSelectionStart({
        accordionKey: selectionEnd.accordionKey,
        row: currentRow,
        column: allColumns[currentColIndex + 1],
      });

      setSelectionEnd({
        accordionKey: selectionEnd.accordionKey,
        row: currentRow,
        column: allColumns[currentColIndex + 1],
      });
    }
  }

  break;

    case "ArrowDown":
      e.preventDefault();
      /* setSelectedCell({
    
        row: currentRow + 1,
        column: selectedCell.column,
        
      }); */
      setSelectionStart({
        accordionKey: selectionEnd.accordionKey,
  row: currentRow + 1,
  column: selectionEnd.column,
});

setSelectionEnd({
  accordionKey: selectionEnd.accordionKey,
  row: currentRow + 1,
  column: selectionEnd.column,
});
      break;

    case "ArrowUp":
      e.preventDefault();
     /*  setSelectedCell({
        row: Math.max(0, currentRow - 1),
        column: selectedCell.column,
      }); */
      setSelectionStart({
        accordionKey: selectionEnd.accordionKey,
 // row: currentRow + 1,
 row: Math.max(0, currentRow - 1),
  column: selectionEnd.column,
});

setSelectionEnd({
  //row: currentRow + 1,
  accordionKey: selectionEnd.accordionKey,
  row: Math.max(0, currentRow - 1),
  column: selectionEnd.column,
});
      break;

    case "ArrowRight":
      e.preventDefault();
      if (currentColIndex < allColumns.length - 1) {
        /* setSelectedCell({
          row: currentRow,
          column: allColumns[currentColIndex + 1],
        }); */
        setSelectionStart({
          accordionKey: selectionEnd.accordionKey,
    row: currentRow,
      column: allColumns[currentColIndex + 1],
});

setSelectionEnd({
  accordionKey: selectionEnd.accordionKey,
  row: currentRow,
      column: allColumns[currentColIndex + 1],
});
      }
      break;

    case "ArrowLeft":
      e.preventDefault();
      if (currentColIndex > 0) {
        /* setSelectedCell({
          row: currentRow,
          column: allColumns[currentColIndex - 1],
        }); */

        setSelectionStart({
          accordionKey: selectionEnd.accordionKey,
   row: currentRow,
      column: allColumns[currentColIndex - 1],
});

setSelectionEnd({
  accordionKey: selectionEnd.accordionKey,
    row: currentRow,
      column: allColumns[currentColIndex - 1],
});
      }
      break;


  }
};

useEffect(() => {
  const handleCopy = (e: ClipboardEvent) => {
    if (!selectionStart || !selectionEnd) return;

    const accordionKey = selectionStart.accordionKey;

    const columns = groupedColumns[accordionKey] ?? [];

    const emptyColumnCount = maxDynamicColumns - columns.length;
    const tableRows = gridRowsByAccordion[accordionKey] ?? [];

    
    const childNames = Array.from(
  new Set(
    tableRows
      .map((r) => r.childName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showChildColumn = childNames.length > 1;

const wgNames = Array.from(
  new Set(
    tableRows
      .map((r) => r.wgName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showWGColumn = wgNames.length > 1;

const workNames = Array.from(
  new Set(
    tableRows
      .map((r) => r.workName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showWorkColumn = workNames.length > 1;
const allColumns = [
  "__rowNumber__",

  ...(showChildColumn ? ["childName"] : []),
  ...(showWGColumn ? ["wgName"] : []),
  ...(showWorkColumn ? ["workName"] : []),

  "notation",
  "takeOff",
  "quantity",

  ...columns.map((c) => c.columnName),
];

const headerRow = [
  "S.No",

  ...(showChildColumn ? ["Child Name"] : []),
  ...(showWGColumn ? ["WG Name"] : []),
  ...(showWorkColumn ? ["Work Name"] : []),

  "Notation",
  "Takeoff",
  "Quantity",

  ...columns.map((c) => c.columnCaption),
];

    const startRow = Math.min(selectionStart.row, selectionEnd.row);
    const endRow = Math.max(selectionStart.row, selectionEnd.row);

   /*  const startCol = Math.min(
      allColumns.indexOf(selectionStart.column),
      allColumns.indexOf(selectionEnd.column)
    );

    const endCol = Math.max(
      allColumns.indexOf(selectionStart.column),
      allColumns.indexOf(selectionEnd.column)
    ); */

    const startIndex = allColumns.indexOf(selectionStart.column);
const endIndex = allColumns.indexOf(selectionEnd.column);

if (startIndex === -1 || endIndex === -1) {
  return;
}

const startCol = Math.min(startIndex, endIndex);
const endCol = Math.max(startIndex, endIndex);

    const copiedRows: string[] = [];

    for (let r = startRow; r <= endRow; r++) {
      const row = tableRows[r];
      if (!row) continue;

      const values: string[] = [];

      for (let c = startCol; c <= endCol; c++) {
        const key = allColumns[c];

        let value: any = "";

        switch (key) {
          case "__rowNumber__":
  value = r + 1;
  break;
          case "childName":
            value = row.childName;
            break;
          case "wgName":
            value = row.wgName;
            break;

          case "workName":
            value = row.workName;
            break;
          case "notation":
            value = row.notation;
            break;
          case "takeOff":
            value = row.takeOff;
            break;
          case "quantity":
            value = row.quantity;
            break;
          default:
            value = row.cells[key];
        }

        values.push(String(value ?? ""));
      }

      copiedRows.push(values.join("\t"));
    }

   /*  e.preventDefault();
    e.clipboardData?.setData("text/plain", copiedRows.join("\n"));
  }; */
 /*  const text = [
  headerRow.join("\t"),
  ...copiedRows,
].join("\n"); */

const selectedHeader = headerRow.slice(
  startCol,
  endCol + 1
);

const text = [
  selectedHeader.join("\t"),
  ...copiedRows,
].join("\n");

e.preventDefault();

e.clipboardData?.setData(
  "text/plain",
  text
);
  };

  window.addEventListener("copy", handleCopy);

  return () => {
    window.removeEventListener("copy", handleCopy);
  };
}, [
  selectionStart,
  selectionEnd,
  groupedColumns,
  gridRowsByAccordion,
]);



 const isCellSelected = (
  accordionKey: string,
  rowIndex: number,
  columnKey: string,
  allColumns: string[]
) => {
  console.log("selectionStart", selectionStart);
console.log("selectionEnd", selectionEnd);
  if (!selectionStart || !selectionEnd) return false;

  if (
  selectionStart.accordionKey !== accordionKey ||
  selectionEnd.accordionKey !== accordionKey
) {
  return false;
}

  const startRow = Math.min(selectionStart.row, selectionEnd.row);
  const endRow = Math.max(selectionStart.row, selectionEnd.row);

  const startCol = Math.min(
    allColumns.indexOf(selectionStart.column),
    allColumns.indexOf(selectionEnd.column)
  );

  const endCol = Math.max(
    allColumns.indexOf(selectionStart.column),
    allColumns.indexOf(selectionEnd.column)
  );

  const currentCol = allColumns.indexOf(columnKey);

  console.log(
  "index",
  selectionStart.column,
  allColumns.indexOf(selectionStart.column)
);

console.log(
  "current",
  columnKey,
  currentCol
);

  const result =
  rowIndex >= startRow &&
  rowIndex <= endRow &&
  currentCol >= startCol &&
  currentCol <= endCol;

if (result) {
  console.log("TRUE CELL", {
    rowIndex,
    columnKey,
    result,
  });
}
console.log({
  rowIndex,
  columnKey,
  startRow,
  endRow,
  startCol,
  endCol,
  currentCol,
  result,
});
return result;

 return (
    rowIndex >= startRow &&
    rowIndex <= endRow &&
    currentCol >= startCol &&
    currentCol <= endCol
  ); 
}; 
/* const isCellSelected = (
  rowIndex: number,
  columnKey: string,
  allColumns: string[]
) => {
  if (!selectionStart || !selectionEnd) return false;

  const startRow = Math.min(selectionStart.row, selectionEnd.row);
  const endRow = Math.max(selectionStart.row, selectionEnd.row);

  const startCol = allColumns.indexOf(selectionStart.column);
  const endCol = allColumns.indexOf(selectionEnd.column);
  const currentCol = allColumns.indexOf(columnKey);

  console.log({
    rowIndex,
    columnKey,
    startCol,
    endCol,
    currentCol,
    allColumns
  });

  return (
    rowIndex >= startRow &&
    rowIndex <= endRow &&
    currentCol >= startCol &&
    currentCol <= endCol
  );
}; */


  const tableKey = (accordionKey: string, columnKey: string) =>
    `${accordionKey}__${columnKey}`;

  const getWidth = (key: string) => columnWidths[key] ?? EXCEL_DEFAULT_COL_WIDTH;

 

// const getTextWidth = (text: string, font = "12px Calibri") => {
const getTextWidth = (
  text: string,
  font = "13px 'Segoe UI'"
) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) return EXCEL_DEFAULT_COL_WIDTH;

  context.font = font;
  return context.measureText(text).width;
};

const autoFitColumn = (
  accordionKey: string,
  columnKey: string,
  headerText: string,
  values: any[]
) => {

  console.log("AutoFit called", accordionKey, columnKey);
  const maxWidth = Math.max(
    getTextWidth(headerText),
    ...values.map((v) => getTextWidth(String(v ?? "")))
  );

 /*  setColumnWidths((prev) => ({
    ...prev,
    [tableKey(accordionKey, columnKey)]: Math.max(
      MIN_WIDTH,
      Math.ceil(maxWidth + 24)
    ),
  })); */
  setColumnWidths((prev) => ({
  ...prev,
 // [columnKey]: Math.max(
 [tableKey(accordionKey, columnKey)]: Math.max(
    MIN_WIDTH,
    Math.ceil(maxWidth + 24)
  ),
}));


};

/* const handleResizeStart = (
  e: React.PointerEvent<HTMLDivElement>,
  key: string
) => {
  e.preventDefault();
  e.stopPropagation();

  dragStartedRef.current = false; */
 const handleResizeStart = (
  e: React.PointerEvent<HTMLDivElement>,
  key: string,
  accordionKey: string,
  columnKey: string,
  headerText: string,
  values: any[]
) => {

  e.preventDefault();
  e.stopPropagation();
  document.body.style.userSelect = "none";
//document.body.style.cursor = "col-resize";

  const now = Date.now();

  if (
    clickColumnRef.current === key &&
    now - lastClickRef.current < 300
  ) {

    lastClickRef.current = 0;

    autoFitColumn(
      accordionKey,
      columnKey,
      headerText,
      values
    );

    return;
  }

  clickColumnRef.current = key;
  lastClickRef.current = now;

  dragStartedRef.current = false;

  

  resizeRef.current = {
    key,
    startX: e.clientX,
    startWidth: columnWidths[key] ?? EXCEL_DEFAULT_COL_WIDTH,
  };

  window.addEventListener("pointermove", handleResizeMove);
  window.addEventListener("pointerup", handleResizeEnd);
};

const handleResizeMove = (e: PointerEvent) => {
  if (!resizeRef.current) return;

  const { key, startX, startWidth } = resizeRef.current;

  const delta = e.clientX - startX;

  // Don't start dragging until the mouse has moved 4 pixels
  if (!dragStartedRef.current) {
    if (Math.abs(delta) < 4) {
      return;
    }

    dragStartedRef.current = true;
   // document.body.style.cursor = "col-resize";
  }

  setColumnWidths(prev => ({
    ...prev,
    [key]: Math.max(
      MIN_WIDTH,
      startWidth + delta
    ),
  }));
};

const handleResizeEnd = () => {

  document.body.style.userSelect = "";
document.body.style.cursor = "";
  resizeRef.current = null;
  dragStartedRef.current = false;

  window.removeEventListener(
    "pointermove",
    handleResizeMove
  );

  window.removeEventListener(
    "pointerup",
    handleResizeEnd
  );
};
  /* const startResize =
    (key: string) =>
    (e: React.PointerEvent<HTMLDivElement>) => 
    {
      e.preventDefault();
      e.stopPropagation();

      resizingRef.current = {
        key,
        startX: e.clientX,
        startWidth: columnWidths[key] ?? EXCEL_DEFAULT_COL_WIDTH,
      };

      const onMove = (ev: PointerEvent) => {
        if (!resizingRef.current) return;

        const { key, startX, startWidth } = resizingRef.current;
        const nextWidth = Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX));

        setColumnWidths((prev) => ({
          ...prev,
          [key]: nextWidth,
        }));
      };

      const onUp = () => {
        resizingRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }; */
   
  

  const formatDisplayValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "";

    if (typeof value === "number" && Number.isFinite(value)) {
      return Number.isInteger(value) ? value.toString() : value.toFixed(2);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") return "";

      const numeric = Number(trimmed);
      if (!Number.isNaN(numeric) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
        return Number.isInteger(numeric) ? numeric.toString() : numeric.toFixed(2);
      }

      return trimmed;
    }

    return String(value);
  };

  const isNumericLike = (value: any) => {
    if (value === null || value === undefined || value === "") return false;
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value === "string") return /^-?\d+(\.\d+)?$/.test(value.trim());
    return false;
  };

  const toExcelValue = (value: any) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "number") return Number.isFinite(value) ? value : "";
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed === "") return "";
      if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
      return trimmed;
    }
    return value;
  };

  const sanitizeFileName = (name: string) =>
    name
      .replace(/[\\/:*?"<>|]+/g, "_")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");

  const handleExportExcel = () => {
    if (openKeys.size === 0) {
      alert("Please expand the accordion card(s) you want to export.");
      return;
    }

    const exportAccordions = accordionList.filter((item) =>
      openKeys.has(item.accordionKey)
    );

    if (exportAccordions.length === 0) {
      alert("Please expand the accordion card(s) you want to export.");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const sheetData: any[][] = [];

    const titleRows: number[] = [];
    const subtitleRows: number[] = [];
    const headerRows: number[] = [];

    exportAccordions.forEach((accordion, index) => {
      const columns = groupedColumns[accordion.accordionKey] ?? [];
      const gridRows = gridRowsByAccordion[accordion.accordionKey] ?? [];

      if (index > 0) {
        sheetData.push([]);
        sheetData.push([]);
      }

      titleRows.push(sheetData.length + 1);
      sheetData.push([`Group ${index + 1}`]);

      subtitleRows.push(sheetData.length + 1);
      sheetData.push([
        `${gridRows.length} row${gridRows.length !== 1 ? "s" : ""} | ${columns.length} col${columns.length !== 1 ? "s" : ""}`,
      ]);

      headerRows.push(sheetData.length + 1);
     /*  sheetData.push([
        "WBS",
        "WG Name",
        "Work Name",
        "Notation",
        "Takeoff",
        "Quantity",
        ...columns.map((col) => col.columnCaption),
      ]); */
      const childNames = Array.from(
  new Set(
    gridRows
      .map(r => r.childName)
      .filter(v => v != null && v !== "")
  )
);

const wgNames = Array.from(
  new Set(
    gridRows
      .map(r => r.wgName)
      .filter(v => v != null && v !== "")
  )
);

const workNames = Array.from(
  new Set(
    gridRows
      .map(r => r.workName)
      .filter(v => v != null && v !== "")
  )
);

const showChildColumn = childNames.length > 1;
const showWGColumn = wgNames.length > 1;
const showWorkColumn = workNames.length > 1;

sheetData.push([
  ...(showChildColumn ? ["Child Name"] : []),
  ...(showWGColumn ? ["WG Name"] : []),
  ...(showWorkColumn ? ["Work Name"] : []),

  "Notation",
  "Takeoff",
  "Quantity",

  ...columns.map(col => col.columnCaption),
]);

      gridRows.forEach((row) => {
      /*   sheetData.push([
          toExcelValue(row.childName),
          toExcelValue(row.wgName),
          toExcelValue(row.workName),
          toExcelValue(row.notation),
          toExcelValue(row.takeOff),
          toExcelValue(row.quantity),
          ...columns.map((col) =>
            // toExcelValue(row.cells[col.columnName])),
          toExcelValue(
    formatDisplayValue(row.cells[col.columnName])
),
          )
        ]); */
        sheetData.push([
  ...(showChildColumn ? [toExcelValue(row.childName)] : []),
  ...(showWGColumn ? [toExcelValue(row.wgName)] : []),
  ...(showWorkColumn ? [toExcelValue(row.workName)] : []),

  toExcelValue(row.notation),
  toExcelValue(row.takeOff),
  toExcelValue(row.quantity),

  ...columns.map((col) =>
    toExcelValue(
      formatDisplayValue(row.cells[col.columnName])
    )
  ),
]);

      });
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    const maxColumns = Math.max(
      5,
      ...exportAccordions.map((accordion) => {
        const columns = groupedColumns[accordion.accordionKey] ?? [];
        return 5 + columns.length;
      })
    );

    worksheet["!cols"] = Array.from({ length: maxColumns }, () => ({
      wch: 10,
    }));

    const applyRowStyle = (rowNumber: number, fillColor: string) => {
      const isHeaderRow = headerRows.includes(rowNumber);
      for (let c = 0; c < maxColumns; c += 1) {
        const cellRef = XLSX.utils.encode_cell({ r: rowNumber - 1, c });
        const cell = worksheet[cellRef];
        if (!cell) continue;
        cell.s = {
          font: { bold: isHeaderRow },
          fill: { patternType: "solid", fgColor: { rgb: fillColor } },
        };
      }
    };

    titleRows.forEach((rowNum) => applyRowStyle(rowNum, "EDEDED"));
    subtitleRows.forEach((rowNum) => applyRowStyle(rowNum, "F7F7F7"));
    headerRows.forEach((rowNum) => applyRowStyle(rowNum, "ECECEC"));

    XLSX.utils.book_append_sheet(workbook, worksheet, "Metrics");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(
      blob,
      `${sanitizeFileName(
        `Metrics_Activity_${activityId ?? "NA"}_WBS_${wbsId ?? "NA"}`
      )}.xlsx`
    );
  };

  useImperativeHandle(ref, () => ({
    exportExcel: handleExportExcel,
  }));

   //const cellBase =
    //"border-r border-b border-[#c7c7c7] px-1 py-[4px] leading-5 whitespace-nowrap overflow-hidden text-ellipsis align-middle bg-white select-none"; 
   const cellBase =
  "border-r border-b border-[#e1e1e1] px-2 py-[1px] h-[20px] leading-[16px] whitespace-nowrap overflow-hidden text-ellipsis align-middle bg-white select-none";
 
    /* const headerBase =
    "relative border-r border-b border-[#c7c7c7] bg-[#ececec] px-1 py-[2px] text-left text-[12px] font-semibold text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis align-middle"; */
const headerBase =
  "relative border-r border-b border-[#d9d9d9] bg-[#f3f3f3] px-2 py-[1px] h-[22px] text-left font-normal text-[#222] whitespace-nowrap overflow-hidden text-ellipsis align-middle";
  // "relative border-r border-b border-[#c7c7c7] bg-white px-1 py-[2px] text-left font-normal text-slate-700 whitespace-nowrap overflow-hidden text-ellipsis align-middle";
  //const renderTable = (accordionKey: string) => {
  
 /*  const renderTable = (
  accordionKey: string,
    showChildColumn: boolean,
  showWGColumn: boolean,
  showWorkColumn: boolean
) => { */
 

const handleSelectAll = (accordionKey: string) => {
  const columns = groupedColumns[accordionKey] ?? [];
  const rows = gridRowsByAccordion[accordionKey] ?? [];

  const childNames = Array.from(
  new Set(
    rows
      .map((r) => r.childName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showChildColumn = childNames.length > 1;

const wgNames = Array.from(
  new Set(
    rows
      .map((r) => r.wgName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showWGColumn = wgNames.length > 1;

const workNames = Array.from(
  new Set(
    rows
      .map((r) => r.workName)
      .filter((v) => v !== null && v !== undefined && v !== "")
  )
);

const showWorkColumn = workNames.length > 1;

  const allColumns = [

     "__rowNumber__",

      ...(showChildColumn ? ["childName"] : []),
  ...(showWGColumn ? ["wgName"] : []),
  ...(showWorkColumn ? ["workName"] : []),
    "notation",
    "takeOff",
    "quantity",
    ...columns.map((c) => c.columnName),
  ];

  setSelectAllTable(accordionKey);

  setSelectionStart({
    accordionKey,
    row: 0,
    column: allColumns[0],
  });

  setSelectionEnd({
    accordionKey,
    row: rows.length - 1,
    column: allColumns[allColumns.length - 1],
  });

   /* const text = rows
    .map((row) =>
      [
        ...(showChildColumn ? [row.childName ?? ""] : []),
  ...(showWGColumn ? [row.wgName ?? ""] : []),
  ...(showWorkColumn ? [row.workName ?? ""] : []),

        row.notation ?? "",
        row.takeOff ?? "",
        row.quantity ?? "",
        ...columns.map((c) => row.cells[c.columnName] ?? ""),
      ]
      .join("\t")
    )
    .join("\n"); */ 
   /*  const text = rows
  .map((row) =>
    [
      ...(showChildColumn
        ? [formatDisplayValue(row.childName)]
        : []),

      ...(showWGColumn
        ? [formatDisplayValue(row.wgName)]
        : []),

      ...(showWorkColumn
        ? [formatDisplayValue(row.workName)]
        : []),

      formatDisplayValue(row.notation),
      formatDisplayValue(row.takeOff),
      formatDisplayValue(row.quantity),

      ...columns.map((c) =>
        formatDisplayValue(row.cells[c.columnName])
      ),
    ].join("\t")
  )
  .join("\n");
    

  navigator.clipboard.writeText(text);
 */
  tableContainerRef.current?.focus();
};

const renderGroup = (
  accordionKey: string,
  showChildColumn: boolean,
  showWGColumn: boolean,
  showWorkColumn: boolean
) => 
{


    const columns = groupedColumns[accordionKey] ?? [];
    const rows = gridRowsByAccordion[accordionKey] ?? [];
  const emptyColumnCount =
    maxDynamicColumns - columns.length;
    const allColumns = [
  "childName",
  "wgName",
  "workName",
  "notation",
  "takeOff",
  "quantity",
  ...columns.map((c) => c.columnName),
];
    return (
      <div className="w-full  overflow-x-auto"
        onMouseUp={() => setIsSelecting(false)}>
         <table className="w-max table-fixed border-collapse text-slate-900"> 
          
          <colgroup>
        <col style={{ width: 50 }} />

          {showChildColumn && (
            <col
  style={{
    width: getWidth(tableKey(accordionKey, "childName")),
  }}
/>
          )}
            {/* <col style={{ width: getWidth(tableKey(accordionKey, "wgName")) }} /> */}
            {showWGColumn && (
    <col
      style={{
        width: getWidth(tableKey(accordionKey, "wgName")),
      }}
    />
  )}

            {/* <col style={{ width: getWidth(tableKey(accordionKey, "workName")) }} /> */}
            {showWorkColumn && (
  <col
    style={{
      width: getWidth(tableKey(accordionKey, "workName")),
    }}
  />
)}
            <col style={{ width: getWidth(tableKey(accordionKey, "notation")) }} />
            <col style={{ width: getWidth(tableKey(accordionKey, "takeoff")) }} />
            <col style={{ width: getWidth(tableKey(accordionKey, "quantity")) }} />
            {columns.map((col) => (
              <col
                key={col.columnName}
                style={{
                  width: getWidth(tableKey(accordionKey, `extra-${col.columnName}`)),
                }}
              />
            ))}

                 {Array.from({ length: emptyColumnCount }).map((_, index) => (
  <col
    key={`empty-col-${index}`}
    style={{
      width: EXCEL_DEFAULT_COL_WIDTH,
    }}
  />
))}
           
          </colgroup>
         

          <thead>
            <tr>
             {/* <th
  className={headerBase}
  style={{
    width: "50px",
    textAlign: "center",
    color: "#808080",
  }}
>
  ◢
</th> */}
{/* <th className={headerBase} */}
<th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
  style={{
    width: "50px",
    textAlign: "center",
    color: "#808080",
    cursor: "pointer",
  }}
  
 /* onMouseDown={() => {
  setSelectAllTable(accordionKey);

  setSelectionStart({
    accordionKey,
    row: 0,
    column: allColumns[0],
  });

  setSelectionEnd({
    accordionKey,
    row: rows.length - 1,
    column: allColumns[allColumns.length - 1],
  });

  tableContainerRef.current?.focus();
}} */
/* onMouseDown={() => {
  setSelectAllTable(accordionKey);

  setSelectionStart({
    accordionKey,
    row: 0,
    column: allColumns[0],
  });

  setSelectionEnd({
    accordionKey,
    row: rows.length - 1,
    column: allColumns[allColumns.length - 1],
  });

  const text = rows
    .map((row) =>
      [
        row.childName ?? "",
        row.wgName ?? "",
        row.workName ?? "",
        row.notation ?? "",
        row.takeOff ?? "",
        row.quantity ?? "",
        ...columns.map(
          (c) => row.cells[c.columnName] ?? ""
        ),
      ].join("\t")
    )
    .join("\n");

  navigator.clipboard.writeText(text);

  tableContainerRef.current?.focus();
}} */
onMouseDown={() => handleSelectAll(accordionKey)}
>
  ◢
</th>



 {showChildColumn && (
  // <th className={headerBase}>

  <th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
  
>
    Child Name

    <div
      
     onPointerDown={(e) =>
    handleResizeStart(
  e,
  tableKey(accordionKey, "childName"),
  accordionKey,
  "childName",
  "Child Name",
  rows.map(r => r.childName)
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      "childName",
      "Child Name",
      rows.map(r => r.childName)
    )
  }
      //className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
    //  className="absolute inset-0 cursor-col-resize"
  className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
      title="Drag to resize"
    />
  </th>
)}
              {showWGColumn && (
  // <th className={headerBase}>
  <th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
>
    WG Name
    <div
      
    onPointerDown={(e) =>
   handleResizeStart(
  e,
  tableKey(accordionKey, "wgName"),
  accordionKey,
  "wgName",
  "WG Name",
  rows.map(r => r.wgName)
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      "wgName",
      "WG Name",
      rows.map(r => r.wgName)
    )
  }

  
      //className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
      className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
      title="Drag to resize"
    />
  </th>
)}
              {showWorkColumn && (
              // <th className={headerBase}>
              <th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
>
                Work Name
                <div
                 onPointerDown={(e) =>
   handleResizeStart(
  e,
  tableKey(accordionKey, "workName"),
  accordionKey,
  "workName",
  "Work Name",
  rows.map(r => r.workName)
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      "workName",
      "Work Name",
      rows.map(r => r.workName)
    )
  }

                 // className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                 className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
                  title="Drag to resize"
                />
              </th>
              )}
{/* 
              <th className={headerBase}> */}

              <th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
>
                Notation
                <div
                 onPointerDown={(e) =>
    handleResizeStart(
  e,
  tableKey(accordionKey, "notation"),
  accordionKey,
  "notation",
  "Notation",
  rows.map(r => r.notation)
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      "notation",
      "Notation",
      rows.map(r => r.notation)
    )
  }
                 // className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                 className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
                  title="Drag to resize"
                />
              </th>

              {/* <th className={headerBase}> */}
              <th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
>
                Takeoff
                <div
                   onPointerDown={(e) =>
   handleResizeStart(
  e,
  tableKey(accordionKey, "takeoff"),
  accordionKey,
  "takeoff",
  "Takeoff",
  rows.map(r => r.takeOff)
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      "takeoff",
      "Takeoff",
      rows.map(r => r.takeOff)
    )
  }

                //  className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                //className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
                className="absolute -right-[4px] top-0 h-full w-[10px] cursor-col-resize z-50"
                  title="Drag to resize"
                />
              </th>

              {/* <th className={`${headerBase} text-right`}> */}
              <th
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
   
>
                Quantity
                <div
                  onPointerDown={(e) =>
    handleResizeStart(
  e,
  tableKey(accordionKey, "quantity"),
  accordionKey,
  "quantity",
  "Quantity",
  rows.map(r => r.quantity)
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      "quantity",
      "Quantity",
      rows.map(r => r.quantity)
    )
  }
  
                 
                 // className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                 className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
                  title="Drag to resize"
                />
              </th>

              {columns.map((col) => {
                const widthKey = tableKey(accordionKey, `extra-${col.columnName}`);
                return (
                 
                   <th
  key={col.columnName}
  className={`${headerBase} ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : ""
  }`}
  title={col.columnCaption}
>
                    {col.columnCaption}
                   {/*  <div
                      onPointerDown={startResize(widthKey)}
                      className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
                      title="Drag to resize"
                    /> */}
                    <div
 onPointerDown={(e) =>
   handleResizeStart(
  e,
  widthKey,
  accordionKey,
  `extra-${col.columnName}`,
  col.columnCaption,
  rows.map(r => r.cells[col.columnName])
)
  }
  onDoubleClick={() =>
    autoFitColumn(
      accordionKey,
      `extra-${col.columnName}`,
      col.columnCaption,
      rows.map(r => r.cells[col.columnName])
    )
  }

  //className="absolute right-0 top-0 h-full w-2 cursor-col-resize"
  className="absolute -right-[2px] top-0 h-full w-[4px] cursor-col-resize"
  title="Drag to resize"
/>
                  </th>
                );
              })}
              {Array.from({ length: emptyColumnCount }).map((_, index) => (
  <th
    key={`empty-header-${index}`}
    className={headerBase}
  />
))}
            </tr>


          </thead>

          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`${accordionKey}-${rowIndex}`}>

            {/*    
                <td
  className={`${cellBase} text-center ${
    selectAllTable === accordionKey
      ? "!bg-[#d9d9d9]"
      : "bg-gray-100"
  }`}
>
  {rowIndex + 1}
</td> */}
<td
  className={`${cellBase} text-center text-[#53538c] !bg-[#e2e2e2]`}
>
  {rowIndex + 1}
</td>
              
{showChildColumn && (
  <td
    title={formatDisplayValue(row.childName)}
    onMouseDown={(e) => {
      e.stopPropagation();
      setSelectAllTable(null);
      setIsSelecting(true);

      setSelectionStart({
        accordionKey,
        row: rowIndex,
        column: "childName",
      });

      setSelectionEnd({
        accordionKey,
        row: rowIndex,
        column: "childName",
      });

      tableContainerRef.current?.focus();
    }}
    onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "childName",
  });
}}
    /* className={`${cellBase}
        ${
        selectionEnd?.row === rowIndex &&
        selectionEnd?.column === "childName"
          ? "bg-[#cce8ff] ring-1 ring-[#4a89ff]"
          : ""
      } 
    `} */
    className={`${cellBase}
  ${
    isCellSelected(
       accordionKey,
      rowIndex,
      "childName",
      allColumns
    )
      ? 
      // "bg-[#cce8ff] ring-1 ring-[#4a89ff]"
      //"bg-[#d9d9d9]"
       "!bg-[#d9d9d9]"
     
      : ""
  }
`}
  >
    {formatDisplayValue(row.childName)}
  </td>
)}

 {showWGColumn && (
  <td
    title={formatDisplayValue(row.wgName)}
    /* onClick={() =>{
      setSelectedCell({
        row: rowIndex,
        column: "wgName",
      });
    tableContainerRef.current?.focus();
    }} */
   onMouseDown={(e) => {
     e.stopPropagation();
     setSelectAllTable(null);
  setIsSelecting(true);

  setSelectionStart({
    accordionKey,
    row: rowIndex,
    column: "wgName",
  });

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "wgName",
  });

  tableContainerRef.current?. focus();
}}
onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "wgName",
  });
}}
    /* className={`${cellBase}
      ${
       
        selectionEnd?.row === rowIndex &&
selectionEnd?.column === "wgName"
          ? "bg-blue-200 outline outline-2 outline-blue-600"
          : ""
      }
    `} */
    className={`${cellBase}
  ${
    isCellSelected(
       accordionKey,
      rowIndex,
      "wgName",
      allColumns
    )
      ? 
     // "bg-[#cce8ff] ring-1 ring-[#4a89ff]"
       "!bg-[#d9d9d9]"
      : ""
  }
`}

  >
    {formatDisplayValue(row.wgName)}
  </td>
)}
               {/*  {showWGColumn && (
  <td
    className={cellBase}
    title={formatDisplayValue(row.wgName)}
  >
    {formatDisplayValue(row.wgName)}
  </td>
)} */}

                {/* <td className={cellBase} title={formatDisplayValue(row.workName)}>
                  {formatDisplayValue(row.workName)}
                </td> */}

                {/* {showWorkColumn && (
  <td
    className={cellBase}
    title={formatDisplayValue(row.workName)}
  >
    {formatDisplayValue(row.workName)}
  </td>
)}
 */}      
 {showWorkColumn && (
  <td
    title={formatDisplayValue(row.workName)}
    /* onClick={() => {
      setSelectedCell({
        row: rowIndex,
        column: "workName",
      });
      tableContainerRef.current?.focus();
    
    }} */
   onMouseDown={(e) => {
     e.stopPropagation();
     setSelectAllTable(null);
  setIsSelecting(true);

  setSelectionStart({
    accordionKey,
    row: rowIndex,
    column: "workName",
  });

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "workName",
  });


  tableContainerRef.current?.focus();
}}
onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "workName",
  });
}}

    className={`${cellBase}
      ${
       
//         selectionEnd?.row === rowIndex &&
// selectionEnd?.column === "workName"
isCellSelected(
   accordionKey,
  rowIndex,
  "workName",
  allColumns
)
          ? 
          //"bg-blue-200 outline outline-2 outline-blue-600"
           "!bg-[#d9d9d9]"
         
          : ""
      }
    `}
  >
    {formatDisplayValue(row.workName)}
  </td>
)}
 
 
             {/*  <td className={cellBase} title={formatDisplayValue(row.notation)}>
                  {formatDisplayValue(row.notation)}
                </td> */}

                <td
  title={formatDisplayValue(row.notation)}
 
 /* onClick={() => {
  setSelectedCell({
    row: rowIndex,
    column: "notation",
  });

  tableContainerRef.current?.focus();
}} */
  onMouseDown={(e) => {
      
     e.stopPropagation();
     setSelectAllTable(null);
  setIsSelecting(true);


  setSelectionStart({
    accordionKey,
    row: rowIndex,
    column: "notation",
  });

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "notation",
  });

  tableContainerRef.current?.focus();
}}
onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "notation",
  });
}}
  className={`${cellBase}
    ${

  isCellSelected(
     accordionKey,
  rowIndex,
  "notation",
  allColumns
) 
//selectionStart?.row === rowIndex &&
//selectionStart?.column === "notation"
        ? 
       
      // "!bg-red-500"
      "!bg-[#d9d9d9]"
        
        : ""
    }
  `}  
  
 // className="bg-red-500"
 
>
  {formatDisplayValue(row.notation)}
</td>
               {/*  <td className={cellBase} title={formatDisplayValue(row.takeOff)}>
                  {formatDisplayValue(row.takeOff)}
                </td> */}
                <td
  title={formatDisplayValue(row.takeOff)}
  /* onClick={() => {
    setSelectedCell({
      row: rowIndex,
      column: "takeOff",
    });
    tableContainerRef.current?.focus();
  }} */
 onMouseDown={(e) => {
   e.stopPropagation();
   setSelectAllTable(null);
  setIsSelecting(true);

  setSelectionStart({
    accordionKey,
    row: rowIndex,
    column: "takeOff",
  });

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "takeOff",
  });

  tableContainerRef.current?.focus();
}}
onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "takeOff",
  });
}}
 

 className={`${cellBase}
    ${
     

isCellSelected(
   accordionKey,
  rowIndex,
  "takeOff",
  allColumns
)
        ? 
        
      // "bg-[#d9d9d9]"
       "!bg-[#d9d9d9]"
       
        : ""
    }
  `}  
  //className={`${cellBase} !bg-yellow-500`}
>
  {formatDisplayValue(row.takeOff)}
</td>
               {/* < td
                  className={`${cellBase} text-right font-semibold`}
                  title={formatDisplayValue(row.quantity)}
                >
                  {formatDisplayValue(row.quantity)}
                </td>
 */}
 <td
  title={formatDisplayValue(row.quantity)}
 /*  onClick={() => {
    setSelectedCell({
      row: rowIndex,
      column: "quantity",
    });
    tableContainerRef.current?.focus();

  }} */
 onMouseDown={(e) => {
   e.stopPropagation();
   setSelectAllTable(null);
  setIsSelecting(true);

  setSelectionStart({
    accordionKey,
    row: rowIndex,
    column: "quantity",
  });

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "quantity",
  });

  tableContainerRef.current?.focus();
}}

onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: "quantity",
  });
}}
  className={`${cellBase} text-right 
  ${
   
     /*  selectionEnd?.row === rowIndex &&
selectionEnd?.column === "quantity" */
isCellSelected(
   accordionKey,
  rowIndex,
  "quantity",
  allColumns
)
        ? 
        //"bg-blue-200 outline outline-2 outline-blue-600"
        //"bg-[#d9d9d9]"
         "!bg-[#d9d9d9]"
        : ""
    }
  `}
>
  {formatDisplayValue(row.quantity)}
</td>
                {columns.map((col) => {
                  const value = row.cells[col.columnName];
                  const numeric = isNumericLike(value);

                  return (
                    
                           <td
  key={col.columnName}
  title={formatDisplayValue(value)} 
 



 onMouseDown={(e) => {
   e.stopPropagation();
   setSelectAllTable(null);
  setIsSelecting(true);

  setSelectionStart({
    accordionKey,
    row: rowIndex,
    column: col.columnName,
  });

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: col.columnName,
  });

  tableContainerRef.current?.focus();
}}
onMouseEnter={() => {
  if (!isSelecting) return;

  setSelectionEnd({
    accordionKey,
    row: rowIndex,
    column: col.columnName,
  });
}}
  className={`${cellBase}
    ${numeric ? "text-right" : "text-left"}
    ${
      
//       selectionEnd?.row === rowIndex &&
// selectionEnd?.column === col.columnName
isCellSelected(
   accordionKey,
  rowIndex,
  col.columnName,
  allColumns
)
        ? 
        //"bg-blue-200 outline outline-2 outline-blue-600"
         "!bg-[#d9d9d9]"
        : ""
    }
  `}
>
  {formatDisplayValue(value)}
</td>
                    
                  
                  
                  );
                })}
                {Array.from({ length: emptyColumnCount }).map((_, index) => (
  <td
    key={`empty-cell-${rowIndex}-${index}`}
    className={cellBase}
  />
))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div
      //className="flex h-full min-h-0 flex-col bg-white"
     ref={tableContainerRef}
tabIndex={0}
onKeyDown={handleKeyDown}

/* onMouseDown={(e) => {
  if (e.target === e.currentTarget) {
    setSelectionStart(null);
    setSelectionEnd(null);
  }
}} */

  
  /*onMouseDown={(e) => {
  if (e.target === e.currentTarget) {
    setSelectionStart(null);
    setSelectionEnd(null);
    setSelectAllTable(null);
  }
}}*/

  className="flex h-full min-h-0 w-full flex-col border border-[#c7c7c7] bg-white"

      // style={{ fontFamily: "Calibri, Arial, sans-serif" }}
      style={{
  fontFamily: "'Segoe UI', sans-serif",
  fontSize: "13px",
  fontWeight: 400,
}}
    >
      <div className="border-b border-[#c7c7c7] bg-white px-2 py-1">
  <div className="flex justify-end">
    <button
      type="button"
      onClick={handleExportExcel}
      disabled={loading || accordionList.length === 0}
      className="inline-flex items-center gap-2 border border-[#c7c7c7] bg-[#f7f7f7] px-2 py-1  text-slate-700 hover:bg-[#f0f0f0] disabled:opacity-50"
    >
      <Download className="h-4 w-4" />
      Export to Excel
    </button>
  </div>
</div>
      

      <div className="min-h-0 flex-1 ">
      
 {/*  onMouseDown={() => {
    setSelectionStart(null);
    setSelectionEnd(null);
  }} */}
        {loading && (
          <div className="m-4 flex items-center gap-2  text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin text-slate-600" />
            Loading metrics...
          </div>
        )}

        {error && (
          <div className="m-4 border border-[#d6a2a2] bg-[#fff4f4] px-3 py-2  text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && accordionList.length === 0 && (
          <div className="m-4 flex h-48 items-center justify-center border border-dashed border-[#c7c7c7]">
            <div className="text-center">
              <Layers3 className="mx-auto mb-2 h-7 w-7 text-slate-400" />
              <p className="text-slate-500">No metrics to display</p>
            </div>
          </div>
        )}
        

       
       {/* <div className="flex-1 min-h-0 overflow-y-auto p-0">*/}
       <div
  className="flex-1 min-h-0 overflow-y-auto p-0"
  onMouseDown={(e) => {
    const target = e.target as HTMLElement;

    if (!target.closest("table")) {
      setSelectionStart(null);
      setSelectionEnd(null);
      setSelectAllTable(null);
    }
  }}
>
         
         {/* <div className="space-y-0 w-full">*/}
         <div className="overflow-auto w-full">
 


            {/* {accordionList.map((item, index) => {
              const isOpen = openKeys.has(item.accordionKey);
              const columns = groupedColumns[item.accordionKey] ?? [];
              const gridRows = gridRowsByAccordion[item.accordionKey] ?? [];
              
              

    const wgNames = Array.from(
  new Set(
    gridRows
      .map(r => r.wgName)
      .filter(v => v !== null && v !== undefined && v !== "")
  )
);


const singleWGName =
  wgNames.length === 1
    ? String(wgNames[0])
    : "";

const showWGColumn = wgNames.length > 1;

const workNames = Array.from(
  new Set(
    gridRows
      .map(r => r.workName)
      .filter(v => v !== null && v !== undefined && v !== "")
  )
);

const singleWorkName =
  workNames.length === 1
    ? String(workNames[0])
    : "";

const showWorkColumn = workNames.length > 1;

const childNames = Array.from(
  new Set(
    gridRows
      .map(r => r.childName)
      .filter(v => v !== null && v !== undefined && v !== "")
  )
);

const singleChildName =
  childNames.length === 1
    ? String(childNames[0])
    : "";

const showChildColumn = childNames.length > 1;

              return (
                <div
                  key={item.accordionKey}
                  className="w-full overflow-hidden border border-[#c7c7c7] bg-white"
                >
                  <button
                    type="button"
                    onClick={() => toggle(item.accordionKey)}
                    
                   className="flex w-full items-center justify-between gap-3 bg-[#e2e2e2] px-3 py-2 text-left hover:bg-[#d9d9d9]"
                 
                  >
                    <div className="flex items-center gap-2">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-slate-700" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-700" />
                      )}

                    
                      <div className="flex items-center gap-3">
  <div className=" text-slate-900">
    

    Group {index + 1} -{" "}

   
   
{[
  item.geometry,
  singleChildName,
  singleWGName,
  singleWorkName
]
  .filter(Boolean)
  .join(" - ")}

  </div>
</div>

                    </div>

                  </button>

                
                  {isOpen && (
  <div
    className="border-t border-[#c7c7c7] bg-white"                                                                           
    onMouseDown={(e) => {
      // clicked empty accordion area
      if (e.target === e.currentTarget) {
        setSelectionStart(null);
        setSelectionEnd(null);
        setSelectAllTable(null);
      }
    }}
  >
    <div>
      
      {renderGroup(
    item.accordionKey,
    showChildColumn,
    showWGColumn,
    showWorkColumn
)}
    </div>
  </div>
)}
                </div>
              );
            })} */}

           {activityId == null || wbsId == null ? (
  <div
    className="flex h-full items-center justify-center text-gray-500"
    style={{
      fontFamily: "'Segoe UI'",
      fontSize: "13px",
      fontWeight: 400,
    }}
  >
    Select a WBS or Activity from the left panel.
  </div>
) : accordionList.length === 0 ? (
  <div
    className="flex h-full items-center justify-center text-gray-500"
    style={{
      fontFamily: "'Segoe UI'",
      fontSize: "13px",
      fontWeight: 400,
    }}
  >
    No NLBD details for the selected node.
  </div>
) : (
<MetricsExcelSheet
    accordionList={accordionList}
    groupedColumns={groupedColumns}
    gridRowsByAccordion={gridRowsByAccordion}
    openKeys={openKeys}

    toggleAccordion={toggle}

    selectionStart={selectionStart}
    selectionEnd={selectionEnd}

    setSelectionStart={setSelectionStart}
    setSelectionEnd={setSelectionEnd}

    isCellSelected={isCellSelected}

    columnWidths={columnWidths}
    getWidth={getWidth}

    handleResizeStart={handleResizeStart}
    autoFitColumn={autoFitColumn}

    formatDisplayValue={formatDisplayValue}

    tableContainerRef={tableContainerRef}

    selectAllTable={selectAllTable}
    setSelectAllTable={setSelectAllTable}

    isSelecting={isSelecting}
    setIsSelecting={setIsSelecting}

    handleKeyDown={handleKeyDown}

    defaultColumnWidth={EXCEL_DEFAULT_COL_WIDTH}

    onSelectAll={handleSelectAll}
/>
)}

          </div>
        </div>
      </div>
    </div>
  );
});

MetricsAccordionTable.displayName = "MetricsAccordionTable";

export default MetricsAccordionTable;
